/**
 * DepartmentSearchDropdown — Fuzzy search dropdown for selecting departments.
 *
 * Uses the same search logic as TopNav department selector:
 * - Acronym matching (FTB, DMV, EDD)
 * - BU codes and department numbers
 * - Partial word matching
 * - Colloquial aliases (unemployment → EDD, prison → CDCR)
 */

import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Search, Building2, ChevronDown, X } from 'lucide-react';
import { buildFuse, searchDepartments } from '../TopNav/departmentSearch';
import './DepartmentSearchDropdown.css';

function DepartmentSearchDropdown({ departments = [], value, onChange, placeholder = 'Search departments...' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Build Fuse index
  const fuse = useMemo(() => buildFuse(departments), [departments]);

  // Get filtered results
  const filteredDepartments = useMemo(() => {
    if (!search.trim()) {
      // Show all departments sorted alphabetically when no search
      return [...departments].sort((a, b) =>
        (a.official_name || a.common_name || '').localeCompare(b.official_name || b.common_name || '')
      );
    }
    return searchDepartments(fuse, search, departments) || [];
  }, [fuse, search, departments]);

  // Find selected department
  const selectedDept = departments.find(d => d.department_id === value);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when opening
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (dept) => {
    onChange(dept.department_id);
    setIsOpen(false);
    setSearch('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setSearch('');
  };

  const displayName = selectedDept
    ? `${selectedDept.acronym ? selectedDept.acronym + ' — ' : ''}${selectedDept.official_name || selectedDept.common_name}`
    : '';

  return (
    <div className="dsd-container" ref={containerRef}>
      <button
        type="button"
        className={`dsd-trigger ${isOpen ? 'dsd-trigger-open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <Building2 size={14} className="dsd-trigger-icon" />
        <span className={`dsd-trigger-text ${!selectedDept ? 'dsd-placeholder' : ''}`}>
          {displayName || placeholder}
        </span>
        {selectedDept ? (
          <button type="button" className="dsd-clear" onClick={handleClear}>
            <X size={12} />
          </button>
        ) : (
          <ChevronDown size={14} className={`dsd-chevron ${isOpen ? 'dsd-chevron-open' : ''}`} />
        )}
      </button>

      {isOpen && (
        <div className="dsd-dropdown">
          <div className="dsd-search">
            <Search size={14} />
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Type to search (acronym, name, BU code)..."
              className="dsd-search-input"
            />
          </div>

          <div className="dsd-list">
            {filteredDepartments.length === 0 ? (
              <div className="dsd-empty">No departments found</div>
            ) : (
              filteredDepartments.slice(0, 50).map((dept) => (
                <button
                  key={dept.department_id}
                  type="button"
                  className={`dsd-item ${dept.department_id === value ? 'dsd-item-selected' : ''}`}
                  onClick={() => handleSelect(dept)}
                >
                  <div className="dsd-item-main">
                    {dept.acronym && <span className="dsd-item-acronym">{dept.acronym}</span>}
                    <span className="dsd-item-name">{dept.official_name || dept.common_name}</span>
                  </div>
                  {dept.bu_code && (
                    <span className="dsd-item-code">BU {dept.bu_code}</span>
                  )}
                </button>
              ))
            )}
            {filteredDepartments.length > 50 && (
              <div className="dsd-more">
                +{filteredDepartments.length - 50} more — refine your search
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default DepartmentSearchDropdown;
